/**
 * Transition Indicator - Compact Click-to-Edit Design
 * Single-row visual indicator that opens modal on click
 */

import React from 'react';
import { Zap, TrendingDown, Sparkles, MoveRight, Loader2 } from 'lucide-react';
import { SceneTransition, TransitionType } from '../../../../types/scene';
import './TransitionIndicator.scss';

interface TransitionIndicatorProps {
  transition?: SceneTransition;
  onClick: () => void;
  onPreview?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const getTransitionIcon = (type: TransitionType, size = 16) => {
  switch (type) {
    case 'cut':
      return <Zap size={size} />;
    case 'fade':
      return <TrendingDown size={size} />;
    case 'crossfade':
      return <Sparkles size={size} />;
    case 'slide':
      return <MoveRight size={size} />;
    default:
      return <Zap size={size} />;
  }
};

const getTransitionColor = (type: TransitionType): string => {
  switch (type) {
    case 'crossfade':
      return '#667eea';
    case 'fade':
      return '#52c41a';
    case 'slide':
      return '#f093fb';
    case 'cut':
      return '#8c8c8c';
    default:
      return '#1890ff';
  }
};

const getTransitionLabel = (type: TransitionType): string => {
  switch (type) {
    case 'cut':
      return 'Instant';
    case 'fade':
      return 'Fade';
    case 'crossfade':
      return 'Crossfade';
    case 'slide':
      return 'Slide';
    default:
      return 'Instant';
  }
};

export const TransitionIndicator: React.FC<TransitionIndicatorProps> = ({
  transition,
  onClick,
  onPreview,
  disabled = false,
  loading = false,
}) => {
  const currentTransition = transition || { type: 'cut' as const, duration: 0, easing: 'ease-in-out' as const };
  const color = getTransitionColor(currentTransition.type);
  const isCut = currentTransition.type === 'cut';

  return (
    <div 
      className={`transition-indicator ${disabled ? 'disabled' : ''} ${loading ? 'loading' : ''}`}
      role="group"
    >
      {/* Visual line/gradient */}
      <div 
        className={`transition-line ${isCut ? 'dashed' : ''}`}
        style={{
          background: isCut ? 'transparent' : `linear-gradient(90deg, ${color}20 0%, ${color} 100%)`,
          borderTop: isCut ? '2px dashed #d9d9d9' : 'none',
        }}
      />

      {/* Badge and Preview Button Container */}
      <div className="transition-controls">
        {/* Compact info badge */}
        <div 
          className="transition-badge"
          onClick={() => !disabled && !loading && onClick()}
          role="button"
          tabIndex={disabled || loading ? -1 : 0}
          title={loading ? "Saving transition..." : "Click to edit transition"}
          style={{ 
            borderColor: color,
            background: '#fff',
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'wait' : 'pointer'
          }}
        >
          {loading ? (
            <div className="transition-icon" style={{ color }}>
              <Loader2 size={14} className="spinner" />
            </div>
          ) : (
            <div className="transition-icon" style={{ color }}>
              {getTransitionIcon(currentTransition.type, 14)}
            </div>
          )}
          <span className="transition-label" style={{ color: '#262626' }}>
            {loading ? 'Saving...' : getTransitionLabel(currentTransition.type)}
          </span>
          {!isCut && !loading && (
            <span 
              className="transition-duration" 
              style={{ 
                color: '#fff',
                background: color,
                padding: '2px 6px',
                borderRadius: '4px'
              }}
            >
              {(currentTransition.duration ?? 0).toFixed(1)}s
            </span>
          )}
        </div>

      </div>
    </div>
  );
};
